/**
 * Popup utilities for window management, dialogs, and modal windows.
 * Provides functions for opening popups, modal dialogs, and handling window positioning.
 */

// External dependencies

/** Media listener interface */
interface MediaListenerManager {
	getListener(id: string): MediaListener | null;
}

/** Media listener interface */
interface MediaListener {
	isModal(): boolean;
	canSelect(): boolean;
	isSimple(): boolean;
	getKey(): string;
	getId(): string;
	getMediaName(): string | null;
	defaultFolder(): string | null;
}

/** Pop-in object interface */
interface PopInObject {
	waiting: boolean;
	loaded: boolean;
	num: number;
	template: string | (() => string);
	width: number;
	height: number;
	margin: number;
	curWidth: number;
	curHeight: number;
	scrollL: number;
	scrollT: number;
	onOpen: () => void;
	onPosition: (w: number, h: number) => void;
	onClose: () => void;
	setup: (template: string | (() => string) | undefined, width: number, height: number, onOpen?: () => void, onClose?: () => void, onPosition?: (w: number, h: number) => void) => void;
	open: () => PopInObject;
	close: () => void;
	build: () => void;
	position: () => void;
}

/** Image preload class interface */
interface ImgPreloadInstance {
	src: string;
	image: HTMLImageElement;
}

/** Dialog args interface */
interface DialogArgs {
	prompt?: string;
	buttons?: string[];
	default?: string;
	cancel?: string;
	args?: string;
	[key: string]: any;
}

/** Extend Window interface for deprecated showModalDialog */
declare global {
	interface Window {
		showModalDialog?: (url: string, args: any, options: string) => any;
		returnValue?: any;
		opera?: { postError: (msg: string) => void };
		popInCur?: number;
	}
}

// Module state
var _MEDIA_JS_LOCAL_: boolean | undefined;
var attribNames: string[] = ["height", "width", "scrollbars", "status", "resizable", "toolbar", "menubar", "location", "directories"];
var waitLoaded: boolean = false;
var isWaiting: boolean = false;

/**
 * Build window attribute string for window.open().
 */
function winAttribs(height?: number, width?: number, scrollbars?: number, status?: number, resizable?: number, toolbar?: number, menubar?: number, location?: number, directories?: number, forEditor?: boolean): string {
	var args: (number | boolean | undefined)[] = [height, width, scrollbars, status, resizable, toolbar, menubar, location, directories, forEditor];
	return addAttribs("", attribNames, args, '=', ',');
}

var dialogNames: string[] = ["dialogHeight", "dialogWidth", "resizable", "scroll", "status", "edge", "help"];

/**
 * Build dialog attribute string for showModalDialog().
 */
function dialogAttribs(dialogHeight?: string, dialogWidth?: string, resizable?: number, scroll?: number | null, status?: number, edge?: string, help?: number): string {
	var args: (string | number | null | undefined)[] = [dialogHeight, dialogWidth, resizable, scroll, status, edge, help];
	return addAttribs("", dialogNames, args, ':', ';');
}

/**
 * Add attributes to a string with separators.
 */
function addAttribs(s: string, names: string[], values: (string | number | boolean | null | undefined)[], vSep: string, aSep: string): string {
	for (var v: string | number | boolean | null | undefined, i: number = 0; i < names.length; i++)
		if ((v = values[i]) != null) s += aSep + names[i] + vSep + (v == 0 ? "no" : (v == 1 ? "yes" : v));
	return s.charAt(0) == aSep ? s.substring(1) : s;
}

// Window preset configurations - Ordered by height, width, var name
var DATE_PICKER: string = winAttribs(140, 180, 0, 0, 1);
var PLEASEWAIT: string = winAttribs(150, 200, 0, 0, 0);
var GLOSSARY: string = winAttribs(150, 400, 1, 1);
var DOC_CHECKOUT: string = winAttribs(200, 400, 0, 0, 0);
var NEWUSER: string = winAttribs(210, 310, 0, 0);
var RELATELISTITEM: string = winAttribs(225, 475, 1, 0, 1);
var TASK_STATUS: string = winAttribs(270, 500, 1, 0, 1);
var MEMBERDIRCALENDAR: string = winAttribs(275, 275, 0, 0, 0);
var MINIHELP: string = winAttribs(300, 400, 1, 0, 0);
var RELATEHINT: string = winAttribs(300, 400, 1, 0, 1, 0, 0, 0, 0);
var DELETE_WEBINFO: string = winAttribs(300, 400, 1, 0, 1);
var MYTEAM_DEMO: string = winAttribs(350, 580, 0, 0, 0);
var CHAT: string = winAttribs(358, 650, 0);
var FRAME_VIEW: string = winAttribs(400, 400, 0, 0, 1);
var ORDER_EDIT: string = FRAME_VIEW;
var DEPENDENCIES: string = winAttribs(400, 500, 1, 1, 1, 0, 0, 0, 0);
var EVENT_VIEW: string = winAttribs(400, 600, 1, 0, 1);
var PRINT_WINDOW: string = winAttribs(400, 680, 1, 0, 1);
var PREFIX_REQUEST: string = winAttribs(400, 800, 1, 1, 1);
var RE_ORDER: string = winAttribs(780, 610, 1, 0, 1);
var TEAM_ADDRESS_BOOK: string = winAttribs(450, 635, 1, 0, 1);
var MEDIA_WIN: string = winAttribs(550, 750, 1, 1, 1, 0, 0, 0, 0);
var TASK_EDIT: string = winAttribs(500, 700, 1, 0, 1);
var DOC_CHECKIN: string = winAttribs(500, 700, 1, 0, 0);
var YELLOWBRIX_WIN: string = winAttribs(510, 660, 1, 0, 1);
var SITEMAP_VIEW: string = winAttribs(550, 500, 0, 0, 1);
var PAGELET_EDIT: string = winAttribs(580, 760, 1, 1, 1);
var GET_RECORD: string = winAttribs(600, 700, 1, 1, 1, 0, 0, 0, 0);
var NORMAL_WIN: string = winAttribs(600, 800, 1, 0, 1);

var HELP_ROOT: string = "ROOT";
var HELP: string = (document.all) ? winAttribs(400, 600, 0, 0, 1) : winAttribs(400, 600, 1, 0, 1, 1);

// Dialog file names
var COLOR_PICKER_DIALOG: string = "colorpicker.jsp";
var SPECTRUM_DIALOG: string = "spectrum.jsp";
var DATE_PICKER_DIALOG: string = "datepicker.jsp";
var FONT_STYLE_PICKER_DIALOG: string = "fontstylepicker.jsp";
var FONT_SIZE_PICKER_DIALOG: string = "fontsizepicker.jsp";
var SPELL_CHECKER_DIALOG: string = "spellchecker.jsp";
var SYMBOL_PICKER_DIALOG: string = "symbolpicker.jsp";
var TEXT_EDITOR_DIALOG: string = "texteditor.jsp";
var TABLE_BUILDER_DIALOG: string = "inserttable.jsp";
var ZESTY_EDITOR_DIALOG: string = "zesty.jsp";
var DATA_PICKER_DIALOG: string = "datapicker.jsp";

// Module state
var firstField: boolean | undefined;
var newWindow: Window | null = null;
var newWindowTime: number = 0;
var APP_ROOT: string = ""; // from common_js.jsp
var pleaseWaitComplete: boolean = false;

// Setter functions to sync module state with globalThis for external access
function setNewWindowTime(time: number): void {
	newWindowTime = time;
	(globalThis as any).newWindowTime = time;
}

function setNewWindow(win: Window | null): void {
	newWindow = win;
	(globalThis as any).newWindow = win;
}

function setFirstField(value: boolean | undefined): void {
	firstField = value;
	(globalThis as any).firstField = value;
}

function setDialogArgs(args: DialogArgs | null): void {
	dialogArgs = args;
	(globalThis as any).dialogArgs = args;
}

function setReturned(fn: ((returnValue: any) => void) | null): void {
	returned = fn;
	(globalThis as any).returned = fn;
}

function setTimeClickURL(url: string | null): void {
	timeClickURL = url;
	(globalThis as any).timeClickURL = url;
}

function setPopInCur(value: number): void {
	popInCur = value;
	(globalThis as any).popInCur = value;
}

/**
 * Open help window.
 */
function getHelp(key: string | null | undefined, contentOnly?: boolean): void {
	var k: string = key as string;
	var c: boolean = (contentOnly != null && contentOnly);
	if (k == null || k == "") k = HELP_ROOT;
	if (document.all) {
		if (!c) doPopup(APP_ROOT + "/help/help.jsp?_event=view&_formClass=myassn.help.HelpSectionWebView&_id=" + k, HELP, false);
		else doPopup(APP_ROOT + "/help/content.jsp?_event=view&_formClass=myassn.help.HelpSectionWebView&_id=" + k, MINIHELP, false);
	} else {
		if (!c) doPopup(APP_ROOT + "/help/help-ns.jsp?_event=view&_formClass=myassn.help.HelpSectionWebView&_id=" + k, HELP, false);
		else doPopup(APP_ROOT + "/help/content.jsp?_event=view&_formClass=myassn.help.HelpSectionWebView&_id=" + k, MINIHELP, false);
	}
}

/**
 * Open a popup window with centering.
 */
function doPopup(url: string, optString: string, isDialogBox?: boolean): void {
	doPopupNoCenter(url, addCentering(optString), isDialogBox);
}

/**
 * Open a popup window without centering.
 */
function doPopupNoCenter(url: string, optString: string, isDialogBox?: boolean): void {
	setNewWindowTime((new Date()).getTime());
	setNewWindow(open(url, "popup" + ((new Date()).getTime()), optString));
	if (newWindow != null) newWindow.focus();
	setFirstField(false);
	window.onfocus = (isDialogBox == true) ? focusPopup : null;
}

/**
 * Show a dialog window.
 */
function showDialog(dialog: string, args: DialogArgs, returned?: (returnValue: any) => void): any {
	var options: string | undefined;
	if (dialog == COLOR_PICKER_DIALOG) options = dialogAttribs("325px", "295px", 0, 0);
	else if (dialog == SPECTRUM_DIALOG) options = dialogAttribs("204px", "295px", 0, 0);
	else if (dialog == DATE_PICKER_DIALOG) options = dialogAttribs("170px", "230px", 0, 0);
	else if (dialog == FONT_STYLE_PICKER_DIALOG) options = dialogAttribs("290px", "200px", 0, 0);
	else if (dialog == FONT_SIZE_PICKER_DIALOG) options = dialogAttribs("280px", "170px", 0, 0);
	else if (dialog == SPELL_CHECKER_DIALOG) {
		var pos: number = navigator.appVersion.indexOf("MSIE");
		var ver: number = parseFloat(navigator.appVersion.substring(pos + 5, pos + 8));
		options = dialogAttribs("400px", "400px", 0, (ver >= 5.5) ? 1 : 0);
	} else if (dialog == SYMBOL_PICKER_DIALOG) options = dialogAttribs("300px", "290px", 0, 1);
	else if (dialog == TEXT_EDITOR_DIALOG) options = dialogAttribs("400px", "500px", 1, 0);
	else if (dialog == TABLE_BUILDER_DIALOG) options = dialogAttribs("225px", "420px", 0, 0);
	else if (dialog == ZESTY_EDITOR_DIALOG) options = dialogAttribs("480px", "640px", 1, 0);
	else if (dialog == DATA_PICKER_DIALOG) { options = dialogAttribs("240px", "375px", 0, 0); dialog += args.args; }
	options += ";status:no;edge:raised;help:no";
	return modalWindow(APP_ROOT + "/jslib/dialogs/" + dialog, args, options!, returned);
}

var dialogArgs: DialogArgs | null = null;
var returned: ((returnValue: any) => void) | null = null;

/**
 * Open a modal window.
 */
function modalWindow(url: string, args: DialogArgs | null, options: string, returnedFn?: (returnValue: any) => void): any {
	if (window.showModalDialog) {
		var rval: any = window.showModalDialog(url, args, options);
		if (returnedFn) {
			returnedFn(rval);
		} else {
			return rval;
		}
	} else {
		options = options.replace(/scroll/g, 'scrollbars');
		options = options.replace(/dialogWidth/g, 'width');
		options = options.replace(/dialogHeight/g, 'height');
		options = options.replace(/px;/g, ',');
		options = options.replace(/;/g, ',');
		options = options.replace(/:/g, '=');
		options += ",modal=yes";
		setDialogArgs(args);
		setReturned(returnedFn || null);
		return window.open(url, "", options);
	}
}

/**
 * Complete a modal window and return a value.
 */
function modalWindowDone(returnValue: any): void {
	if (window.showModalDialog) {
		window.returnValue = returnValue;
	} else if (window.opener && (window.opener as any).returned) {
		(window.opener as any).returned(returnValue);
	}
	window.close();
}

/**
 * Open the media library.
 */
function openMediaLibrary(listener: string | MediaListener | null, modal?: boolean | null, select?: boolean | null, simple?: boolean | null, mediaEditKey?: string, folderName?: string | null, layout?: string | null): void {
	var key: string = MEDIA_LIBRARY_KEY, url: string = APP_ROOT + "/shared/files/medialibrary.jsp?_event=view&_id=";
	if (typeof (listener) == "string" && typeof (_MEDIA_JS_) != "undefined")
		listener = mediaListenerManager.getListener(listener);
	if (listener != null) {
		var mediaListener = listener as MediaListener;
		if (modal == null) modal = mediaListener.isModal();
		if (select == null) select = mediaListener.canSelect();
		if (simple == null) simple = mediaListener.isSimple();
		key = mediaListener.getKey();
		url += key;
		if (select != false) {
			url += "&_listenerId=" + mediaListener.getId();
		}
		var mediaName: string | null = mediaListener.getMediaName();
		if (mediaName != null && mediaName.length > 0)
			url += "&_mediaName=" + mediaName;
		if (folderName == null) {
			folderName = mediaListener.defaultFolder();
			if (folderName != null) {
				url += "&_openFolder=true";
			}
		}
		if (folderName != null)
			url += "&_folderName=" + folderName;
	} else {
		url += key;
	}
	if (simple == true) {
		url += "&_simple=true";
	}
	if (mediaEditKey) {
		url += "&_mediaEditKey=" + mediaEditKey;
	}
	if (layout != null) {
		url += "&_layout=" + layout;
	}
	if (navigator.appVersion.indexOf("MSIE") == -1 || modal != true) {
		doPopup(url, MEDIA_WIN, modal == true);
	} else {
		url += "&_modal=true";
		var returnVal: any = modalWindow(url, null, dialogAttribs("450px", "750px", 1, null, 0, "raised", 0));
		if (_MEDIA_JS_ == true && returnVal != null) {
			mediaCallback(key, returnVal.mediaListKey, listener == null ? null : (listener as MediaListener).getId(), returnVal.mediaItemKey);
		}
	}
}

/**
 * Open the media library for a TinyMCE editor.
 */
function openMediaLibraryForEditor(ed: any): void {
	var key: string = MEDIA_LIBRARY_KEY, url: string = APP_ROOT + "/shared/files/medialibrary.jsp?_event=view&_id=";
	var listener: MediaListener | null = mediaListenerManager.getListener(ed.id);
	var select: boolean | null | undefined, simple: boolean | null | undefined, mediaEditKey: string | undefined, folderName: string | null | undefined;
	if (listener != null) {
		if (select == null) select = listener.canSelect();
		if (simple == null) simple = listener.isSimple();
		key = listener.getKey();
		url += key;
		if (select != false) {
			url += "&_listenerId=" + listener.getId();
		}
		var mediaName: string | null = listener.getMediaName();
		if (mediaName != null && mediaName.length > 0)
			url += "&_mediaName=" + mediaName;
		if (folderName != null)
			url += "&_folderName=" + folderName;
	} else {
		url += key;
	}
	if (simple == true) {
		url += "&_simple=true";
	}
	if (mediaEditKey) {
		url += "&_mediaEditKey=" + mediaEditKey;
	}
	url += "&_modal=false&_editor=true";
	ed.windowManager.open({
		title: "Media Library",
		url: url,
		width: 750,
		height: 450,
		inline: 1,
		resizable: true,
		maximizable: true
	}, null);
}

/**
 * Show a prompt dialog.
 */
function uberPrompt(promptHTML: string, buttons: string[], width: number, height: number, defaultButton?: string, cancelButton?: string): any {
	var options: string = dialogAttribs(height + "px", width + "px", 0, null, 0, "raised", 0);
	var args: DialogArgs = { prompt: promptHTML, buttons: buttons, "default": defaultButton, cancel: cancelButton };
	return modalWindow(APP_ROOT + "/jslib/dialogs/prompt.jsp", args, options);
}

/**
 * Open a popup with two frames.
 */
function doPopupFrame(title: string, urlOne: string, urlTwo: string, optString: string, isDialogBox?: boolean): void {
	return doPopup(APP_ROOT + "/jslib/popframe.jsp?title=" + escape(title) + "&urlOne=" + escape(urlOne) + "&urlTwo=" + escape(urlTwo), optString, isDialogBox);
}

/**
 * Open a popup at the event position.
 */
function doPopupAtEvent(url: string, optString: string, isDialogBox: boolean | undefined, e: MouseEvent & { offsetX?: number; offsetY?: number }, offset?: number): void {
	var absX: number, absY: number, h: number, y: number;
	if (document.all) {
		absX = e.screenX - (e.offsetX || 0);
		absY = e.screenY - (e.offsetY || 0);
	} else {
		absX = e.screenX;
		absY = e.screenY;
		offset = 0;
	}
	h = parseInt(parseValueOf(optString, "height"));
	y = absY + (offset != null ? offset : 0);
	if (y + h > screen.availHeight) y = absY - h;
	return doPopupNoCenter(url, addPosition(optString, absX, y), isDialogBox);
}

/**
 * Show please wait dialog and navigate to URL.
 */
function pleaseWait(url: string | null, message?: string | null): void {
	if (message == null) message = "Working";
	popupWait(message);
	if (url != null) {
		(window.location as any) = url;
	}
}

// Not needed for h2. Use if static resources are ever pushed to a CDN
var BASE_STATIC_RESOURCE: string = "";

/**
 * Image preload constructor.
 */
function ImgPreload(this: ImgPreloadInstance, src: string): void {
	this.src = src;
	this.image = new Image();
	this.image.src = src;
}

var TRANSPARENT_IMG: ImgPreloadInstance = new (ImgPreload as any)(BASE_STATIC_RESOURCE + '/images/common/transparent.gif');
var PLEASE_WAIT_IMG: ImgPreloadInstance = new (ImgPreload as any)(BASE_STATIC_RESOURCE + '/images/common/pleasewait.gif');
var CANCEL_IMG: ImgPreloadInstance = new (ImgPreload as any)(BASE_STATIC_RESOURCE + '/images/buttons/btn_cancel.gif');
var SEMI_TRANS_IMG: ImgPreloadInstance = new (ImgPreload as any)(BASE_STATIC_RESOURCE + '/images/common/semitransblue.png');

var WAIT_DEFAULT_CONTENT: string = '<table align="center" width="200" height="200" cellpadding="0" cellspacing="0" border="0" style="border:solid #000000 1px;background-color:#FFFFFF">'
	+ '<tr><td><img src="' + TRANSPARENT_IMG.src + '" height="24" width="24" border="0"/></td></tr>'
	+ '<tr><td align="center" class="formLabel" id="waitMessage"></td></tr><tr><td align="center">'
	+ '<img src="' + PLEASE_WAIT_IMG.src + '" width="140" height="42" vspace="3" border="0" alt="Please Wait"></td></tr>'
	+ '<tr><td align="center"><a href="javascript:genericClose();"><img src="' + CANCEL_IMG.src + '" vspace="3" border="0" alt="Cancel"></a></td></tr>'
	+ '</table>';

var WAIT_DEFAULT_CONTENT_MANAGE: string = ''
	+ '<div class="loader-block">'
	+ '<div  style="color: white; text-align: center; font-size: x-large;">Please Wait</div>'
	+ '<div id="waitMessage" style="color: white;text-align: center;"></div>'
	+ '<div class="loader"></div>'
	+ '<div style="text-align: center;margin-bottom: 15px;"><a href="javascript:genericClose();" class="btn btn-default btn-xs">Close</a></div></div>';

/**
 * Helper to check if we're in manage/admin mode (brandBluestep element exists)
 */
function isManageMode(): boolean {
	return document.getElementById("brandBluestep") !== null;
}

/**
 * Show a popup wait dialog.
 */
function popupWait(message: string, template?: string | (() => string), width?: number, height?: number): void {
	var w: number = width != null ? width : (isManageMode() ? 300 : 200);
	var h: number = height != null ? height : (isManageMode() ? 330 : 200);
	getPopInObj(template, w || 200, h || 200, function (this: PopInObject) {
		const waitMessageEl = document.getElementById("waitMessage" + this.num);
		if (waitMessageEl) waitMessageEl.textContent = message;
	}).open();
}

/**
 * Open a pop-in iframe.
 */
function doPopIn(url: string, h: number, w: number, topClose?: boolean, refreshParent?: boolean): void {
	getPopIn(url, h, w, topClose, refreshParent);
}

/**
 * Get a pop-in iframe object.
 */
function getPopIn(url: string, h: number, w: number, topClose?: boolean, refreshParent?: boolean): PopInObject {
	var win: Window = window, origWin: Window = win;
	try {
		var parentLoc: string = win.parent.location.href;
		while (parentLoc != win.location.href && parentLoc.indexOf("/jslib/popframe.jsp") == -1 && typeof (win.parent as any).doPopIn == "function") {
			win = win.parent;
			parentLoc = win.parent.location.href;
		}
	} catch (ex) { }
	var b: string = '', a: string = '';
	if (topClose === false) {
		a = '<div onclick="genericClose()" style="text-align:center;cursor:pointer"><img src="/images/buttons/btn_close.gif" alt="Close" border="0" vspace="0"></div>';
	} else {
		b = '<div style="text-align:right"><img src="/images/buttons/btn_karatdelete.gif" alt="Close" style="border:0;margin:3px 4px 1px 4px;cursor:pointer" onclick="genericClose()"></div>';
	}
	return (win as any).getPopInObj(
		function (this: PopInObject) {
			return '<div id="popInDiv' + this.num + '" style="position:absolute;width:150px;height:150px;border: solid black 1px; background-color:#2f2f3f;border-radius:10px">' + b
				+ '<div style="border:solid white 1px;margin:4px;padding:5px;background-color:white;border-radius:6px"><iframe id="popInWindow' + this.num
				+ '" style="width:130px;height:110px;border:none;background-color:white" scrolling="auto" src="' + url + '"></iframe></div>' + a + '</div>';
		},
		w, h,
		function (this: PopInObject) {
			const popInWindow = (win as any).document.getElementById("popInWindow" + this.num) as HTMLIFrameElement | null;
			if (popInWindow) {
				popInWindow.setAttribute("src", url);
				if (popInWindow.contentWindow) {
					(popInWindow.contentWindow as any).opener = origWin;
				}
			}
		},
		refreshParent ? genericRefresh : function (this: PopInObject) {
			const popInDiv = (win as any).document.getElementById("popInDiv" + this.num) as HTMLElement | null;
			if (popInDiv) popInDiv.innerHTML = "Closed";
		},
		function (this: PopInObject, w: number, h: number) {
			const popInDiv = (win as any).document.getElementById("popInDiv" + this.num) as HTMLElement | null;
			const popInWindow = (win as any).document.getElementById("popInWindow" + this.num) as HTMLElement | null;
			if (popInDiv) {
				popInDiv.style.width = w + "px";
				popInDiv.style.height = h + "px";
			}
			if (popInWindow) {
				popInWindow.style.width = (w - 20) + "px";
				popInWindow.style.height = (h - 40) + "px";
			}
		}
	).open();
}

/**
 * Refresh the current page.
 */
function genericRefresh(): void {
	if (popInCur >= 0) {
		var p: PopInObject = popInStack[popInCur];
		const popInMain = document.getElementById("popInMain" + p.num);
		const iframe = popInMain?.querySelector("iframe") as HTMLIFrameElement | null;
		if (iframe) {
			(iframe.contentWindow as any).genericRefresh();
			return;
		}
	}
	if (typeof refreshPage == 'function' && refreshPage()) {
		return;
	}
	if (typeof _ERROR_REPORTER_ == 'boolean' && curFormObj != null && curFormObj.getElement("_postEvent") != null) {
		var url: string = location.href, pos: number = url.indexOf('?');
		if (pos == -1) pos = url.indexOf("#");
		if (pos != -1) url = url.substring(0, pos);
		disableValidation();
		submitForm(null, 'persist', url + '?__scrollPos=' + pagePosTop());
	} else {
		location.reload();
	}
}

/**
 * Close the current pop-in or window.
 */
function genericClose(refresh?: boolean): void {
	if (popInCur >= 0) {
		const popInMain = document.getElementById("popInMain" + popInCur);
		const iframe = popInMain?.querySelector("iframe") as HTMLIFrameElement | null;
		if (iframe) {
			try {
				var frameWin: any = iframe.contentWindow;
				if (typeof frameWin._CM_ == "object" && typeof frameWin.fieldValueChangedCheck == "function") {
					if (frameWin.fieldValueChangedCheck() === false) return;
				}
			} catch (err) { /*do nothing*/ }
		}
		popInStack[popInCur].close();
		setPopInCur(popInCur - 1);
		if (refresh === true) genericRefresh();
	} else if (typeof parent.popInCur == "number" && parent.popInCur >= 0) {
		(parent as any).genericClose(refresh);
	} else if (opener) {
		if (refresh) (opener as any).genericRefresh();
		window.close();
	} else {
		window.close();
	}
}

var closeWait: typeof genericClose = genericClose;

var popInStack: PopInObject[] = [];
var popInCur: number = -1;
var nullFn: () => void = function () { };

/**
 * Get or create a pop-in object.
 */
function getPopInObj(template: string | (() => string) | undefined, width: number, height: number, onOpen?: () => void, onClose?: () => void, onPosition?: (w: number, h: number) => void): PopInObject {
	setPopInCur(popInCur + 1);
	var rtnVal: PopInObject = popInStack[popInCur];
	if (!rtnVal) {
		rtnVal = {
			waiting: false,
			loaded: false,
			num: popInCur,
			template: isManageMode() ? WAIT_DEFAULT_CONTENT_MANAGE : WAIT_DEFAULT_CONTENT,
			width: 200,
			height: 200,
			margin: 25,
			curWidth: 0,
			curHeight: 0,
			scrollL: 0,
			scrollT: 0,
			onOpen: nullFn,
			onPosition: nullFn,
			onClose: nullFn,
			setup: function (this: PopInObject, template: string | (() => string) | undefined, width: number, height: number, onOpen?: () => void, onClose?: () => void, onPosition?: (w: number, h: number) => void): void {
				var t: PopInObject = this;
				t.template = template || (isManageMode() ? WAIT_DEFAULT_CONTENT_MANAGE : WAIT_DEFAULT_CONTENT).replace('id="waitMessage"', 'id="waitMessage' + t.num + '"');
				t.width = width;
				t.height = height;
				t.onOpen = onOpen || nullFn;
				t.onPosition = onPosition || nullFn;
				t.onClose = onClose || nullFn;
				if (t.num == 0) {
					t.scrollT = pagePosTop();
					t.scrollL = pagePosLeft();
				}
				t.margin = 25;
			},
			open: function (this: PopInObject): PopInObject {
				var t: PopInObject = this;
				t.waiting = true;
				t.build();
				t.position();
				const popInMain = document.getElementById("popInMain" + t.num);
				const disableImg = document.getElementById("disableImg" + t.num);
				if (popInMain) popInMain.style.visibility = "visible";
				if (disableImg) disableImg.style.visibility = "visible";
				t.onOpen();
				return this;
			},
			close: function (this: PopInObject): void {
				var t: PopInObject = this;
				const popInMain = document.getElementById("popInMain" + t.num);
				const disableImg = document.getElementById("disableImg" + t.num);
				if (popInMain) popInMain.style.visibility = "hidden";
				if (disableImg) {
					disableImg.style.width = "0px";
					disableImg.style.height = "0px";
					disableImg.style.visibility = "hidden";
				}
				t.waiting = false;
				if (t.num == 0) window.scrollTo(t.scrollL, t.scrollT);
				t.onClose();
			},
			build: function (this: PopInObject): void {
				var t: PopInObject = this;
				if (!t.loaded) {
					const popInMainDiv = document.createElement('div');
					popInMainDiv.id = "popInMain" + t.num;
					popInMainDiv.style.cssText = "position:absolute;top:300px;left:300px;z-index:" + (t.num * 2 + 9999) + ";visibility:hidden";
					document.body.appendChild(popInMainDiv);

					const disableImg = document.createElement('img');
					disableImg.id = "disableImg" + t.num;
					disableImg.src = SEMI_TRANS_IMG.src;
					disableImg.setAttribute("border", "0");
					disableImg.style.cssText = "position:absolute;top:0px;left:0px;width:0px;height:0px;z-index:" + (t.num * 2 + 998) + ";visibility:hidden";
					document.body.appendChild(disableImg);

					window.addEventListener("resize", function () { t.position(); });
					window.addEventListener("scroll", function () { t.position(); });
					t.loaded = true;
				}
				const popInMain = document.getElementById("popInMain" + t.num);
				if (popInMain) popInMain.innerHTML = typeof t.template == "function" ? t.template() : t.template;
			},
			position: function (this: PopInObject): void {
				var t: PopInObject = this;
				if (t.waiting) {
					var windowHeight: number = window.innerHeight,
						windowWidth: number = window.innerWidth,
						windowScrollTop: number = window.pageYOffset || document.documentElement.scrollTop,
						windowScrollLeft: number = window.pageXOffset || document.documentElement.scrollLeft,
						h: number = Math.min(t.height || 10000, windowHeight - t.margin * 2),
						w: number = Math.min(t.width || 10000, windowWidth - t.margin * 2);
					const disableImg = document.getElementById("disableImg" + t.num);
					const popInMain = document.getElementById("popInMain" + t.num);
					if (disableImg) {
						disableImg.style.width = (windowWidth + windowScrollLeft) + "px";
						disableImg.style.height = (windowHeight + windowScrollTop) + "px";
					}
					if (popInMain) {
						popInMain.style.top = parseInt(String((windowHeight - h) / 2 + windowScrollTop)) + "px";
						popInMain.style.left = parseInt(String((windowWidth - w) / 2 + windowScrollLeft)) + "px";
					}
					t.onPosition(w, h);
				}
			}
		};
		popInStack[popInCur] = rtnVal;
	}
	rtnVal.setup(template, width, height, onOpen, onClose, onPosition);
	return rtnVal;
}

/**
 * Focus the popup window.
 */
function focusPopup(): void {
	if (newWindow != null && !newWindow.closed) newWindow.focus();
}

/**
 * Close the popup window with a delay.
 */
function closePopup(): void {
	if (newWindow != null && (new Date()).getTime() - newWindowTime > 500) window.setTimeout(realClosePopup, 100);
}

/**
 * Actually close the popup window.
 */
function realClosePopup(): void {
	if (newWindow != null && !newWindow.closed) {
		newWindow.close();
	}
	setNewWindow(null);
}

/**
 * Open a glossary popup.
 */
function glossaryPopup(keyword: string): void {
	var inKey: string = keyword;
	var outKey: string = "";
	for (var i: number = 0; i < inKey.length; i++) {
		if (inKey.charAt(i) == " ") outKey += "+";
		else outKey += inKey.charAt(i);
	}
	return doPopup("/member/template/glossaryPopup.jsp?keyword=" + outKey, GLOSSARY);
}

/**
 * Add centering to window options.
 */
function addCentering(optString: string): string {
	var width: string = parseValueOf(optString, "width");
	var height: string = parseValueOf(optString, "height");
	if (windowOnMainMonitor()) {
		if (width > "") width = "" + parseInt(String(centerLength(screen.width, parseInt("0" + width, 10))));
		if (height > "") height = "" + parseInt(String(centerLength(screen.height, parseInt("0" + height, 10))));
	} else {
		if (width > "") width = "" + parseInt(String(centerLength(pageWidth(), parseInt("0" + width, 10)) + windowPosLeft()));
		if (height > "") height = "" + parseInt(String(centerLength(pageHeight(), parseInt("0" + height, 10)) + windowPosTop()));
	}
	return addPosition(optString, width, height);
}

/**
 * Check if window is on main monitor.
 */
function windowOnMainMonitor(): boolean {
	var area: number = pageWidth() * pageHeight();
	var height: number = makeBetween(0, screenHeight(), windowPosTop() + pageHeight()) - makeBetween(0, screenHeight(), windowPosTop());
	var width: number = makeBetween(0, screenWidth(), windowPosLeft() + pageWidth()) - makeBetween(0, screenWidth(), windowPosLeft());
	return (height * width * 1.5) / (pageWidth() * pageHeight() * 1.5) > 0.5;
}

/**
 * Clamp a value between lo and hi.
 */
function makeBetween(lo: number, hi: number, val: number): number {
	if (val < lo) return lo;
	if (val > hi) return hi;
	return val;
}

/**
 * Parse a value from an option string.
 */
function parseValueOf(optString: string, param: string): string {
	var begin: number = optString.indexOf(param + "=");
	while (begin > 0 && optString.charAt(begin - 1) != ',') begin = optString.indexOf(param + "=", begin + 1);
	if (begin == -1) return "";
	begin += param.length + 1;
	var end: number = optString.indexOf(",", begin);
	if (end == -1) end = optString.length;
	return optString.substring(begin, end);
}

/**
 * Calculate center position.
 */
function centerLength(fullLen: number, subLen: number): number {
	return (fullLen - subLen) / 2;
}

/**
 * Add position to window options.
 */
function addPosition(optString: string, screenX: string | number, screenY: string | number): string {
	if (navigator.userAgent.indexOf("AOL") > -1) return optString;
	if (document.all) {
		if (parseValueOf(optString, "left") == "" && screenX > "") optString += ",left=" + screenX;
		if (parseValueOf(optString, "top") == "" && screenY > "") optString += ",top=" + screenY;
	} else {
		if (parseValueOf(optString, "screenX") == "" && screenX > "") optString += ",screenX=" + screenX;
		if (parseValueOf(optString, "screenY") == "" && screenY > "") optString += ",screenY=" + screenY;
	}
	return optString;
}

var timeClickURL: string | null = null;

/**
 * Strip domain from URL.
 */
function stripDomain(url: string): string {
	if (url.indexOf("http") == 0) {
		url = url.substring(url.indexOf("/", 8));
	}
	return url;
}

/**
 * Handle click navigation with debouncing.
 */
function goClick(url: string): void {
	if (timeClickURL == url) {
		return;
	}
	setTimeClickURL(url);
	setTimeout(function () { setTimeClickURL(null); }, 2000);
	if (url.substring(0, 11).toLowerCase() == "javascript:") {
		// Execute javascript: URLs using Function constructor instead of eval
		new Function(url.substring(11))();
	} else {
		(document.location as any) = url;
	}
}

/**
 * Cancel click if already clicked recently.
 */
function cancelClick(anchor: HTMLAnchorElement): boolean {
	var url: string = stripDomain(anchor.href);
	if (timeClickURL == url) {
		return false;
	}
	setTimeClickURL(url);
	setTimeout(function () { setTimeClickURL(null); }, 2000);
	return true;
}

/**
 * Get page width.
 */
function pageWidth(): number {
	return window.innerWidth;
}

/**
 * Get page height.
 */
function pageHeight(): number {
	return window.innerHeight;
}

/**
 * Get page scroll left position.
 */
function pagePosLeft(): number {
	return window.pageXOffset || document.documentElement.scrollLeft;
}

/**
 * Get page scroll top position.
 */
function pagePosTop(): number {
	return window.pageYOffset || document.documentElement.scrollTop;
}

/**
 * Get page right edge position.
 */
function pagePosRight(): number {
	return pagePosLeft() + pageWidth();
}

/**
 * Get page bottom edge position.
 */
function pagePosBottom(): number {
	return pagePosTop() + pageHeight();
}

/**
 * Get window top position on screen.
 */
function windowPosTop(): number {
	return window.screenTop !== undefined ? window.screenTop : window.screenY;
}

/**
 * Get window left position on screen.
 */
function windowPosLeft(): number {
	return window.screenLeft !== undefined ? window.screenLeft : window.screenX;
}

/**
 * Get screen height.
 */
function screenHeight(): number {
	return screen ? screen.availHeight : 600;
}

/**
 * Get screen width.
 */
function screenWidth(): number {
	return screen ? screen.availWidth : 800;
}

/**
 * Log a message to console or available output.
 */
function logIt(msg: string): void {
	if (typeof (console) !== "undefined") console.log(msg);
	else if (typeof (window.opera) !== "undefined") window.opera!.postError(msg);
	else alert(msg);
}

/**
 * Send a session event to the server.
 */
function sendSessionEvent(key: string, data: string | string[]): void {
	var encodedKey: string = encodeURIComponent(key);
	var url: string = "/se?t=session&k=" + encodedKey + "&_=" + new Date().getTime();
	if (data instanceof Array) {
		for (var i in data) {
			url += "&d=" + encodeURIComponent(data[i]);
		}
	} else {
		url += "&d=" + encodeURIComponent(data);
	}
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), 30000);
	fetch(url, { signal: controller.signal })
		.finally(() => clearTimeout(timeoutId));
}

/**
 * Subscribe to session events.
 */
function sessionEvent(key: string, fn: (data: any) => void, clear?: boolean, lastEventID?: string, fixedDelay?: number): void {
	serverEvent("session", key, fn, clear, lastEventID, fixedDelay);
}

/**
 * Subscribe to server events.
 */
function serverEvent(type: string, key: string, fn: (data: any) => void, clear?: boolean, lastEventID?: string, fixedDelay?: number): void {
	// Use EventSource if defined otherwise use fetch for Long Polling
	var WAIT: number = 45, encodedKey: string = encodeURIComponent(key), encodedType: string = encodeURIComponent(type);
	fixedDelay = fixedDelay || 0;
	var url: string = "/se?t=" + encodedType + "&k=" + encodedKey + "&w=" + WAIT + "&r=" + fixedDelay + "&c=" + (clear === true) + "&_=" + new Date().getTime() + (lastEventID === undefined ? "" : "&last-event-id=" + lastEventID);
	if (typeof (EventSource) !== "undefined" && navigator.userAgent.toLowerCase().indexOf('firefox') <= -1) {
		new EventSource(url)
			.onmessage = function (event: MessageEvent) {
				var vals: any = JSON.parse(event.data);
				for (var i in vals) {
					fn(vals[i]);
				}
			};
	} else {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), WAIT * 1000);
		fetch(url, { signal: controller.signal })
			.then(response => response.text())
			.then(function (data: string) {
				var streamData: string[] = data.split("\n\n"),
					parsedData: { data: any; event: string | null }[] = [];
				for (var i in streamData) {

					if (streamData[i]) {
						var streamItem: string[] = streamData[i].split("\n");
						var parsedItem: { data: any; event: string | null } = {
							data: "",
							event: null
						};
						for (var j in streamItem) {
							if (/retry: /.test(streamItem[j])) {
								fixedDelay = parseInt(streamItem[j].split("retry: ")[1]);
							} else if (/id: /.test(streamItem[j])) {
								lastEventID = streamItem[j].split("id: ")[1];
							} else if (/data: /.test(streamItem[j])) {
								parsedItem.data += streamItem[j].split("data: ")[1];
							} else if (/event: /.test(streamItem[j])) {
								parsedItem.event = streamItem[j].split("event: ")[1];
							}
						}
						if (parsedItem.data) {
							parsedItem.data = JSON.parse(parsedItem.data);
						}
						parsedData.push(parsedItem);
					}
				}

				for (var i in parsedData) {
					for (var j in parsedData[i].data) {
						if (parsedData[i].data[j]) {
							fn(parsedData[i].data[j]);
						}
					}
				}
			})
			.finally(function () {
				clearTimeout(timeoutId);
				setTimeout(function () {
					serverEvent(type, key, fn, clear, lastEventID, fixedDelay);
				}, fixedDelay);
			});
	}
}

/**
 * Get a stack trace.
 */
var getStackTrace: () => string[] = (function () {

	var mode: string;
	try { (0 as any)(); } catch (e: any) {
		mode = e.stack ? 'Firefox' : window.opera ? 'Opera' : 'Other';
	}

	switch (mode!) {
		case 'Firefox': return function (): string[] {
			try { (0 as any)(); } catch (e: any) {
				return e.stack.replace(/^.*?\n/, '').
					replace(/(?:\n@:0)?\s+$/m, '').
					replace(/^\(/gm, '{anonymous}(').
					split("\n");
			}
			return [];
		};

		case 'Opera': return function (): string[] {
			try { (0 as any)(); } catch (e: any) {
				var lines: string[] = e.message.split("\n"),
					ANON: string = '{anonymous}',
					lineRE: RegExp = /Line\s+(\d+).*?in\s+(http\S+)(?:.*?in\s+function\s+(\S+))?/i,
					i: number, j: number, len: number;

				for (i = 4, j = 0, len = lines.length; i < len; i += 2) {
					if (lineRE.test(lines[i])) {
						lines[j++] = (RegExp.$3 ?
							RegExp.$3 + '()@' + RegExp.$2 + RegExp.$1 :
							ANON + RegExp.$2 + ':' + RegExp.$1) +
							' -- ' + lines[i + 1].replace(/^\s+/, '');
					}
				}

				lines.splice(j, lines.length - j);
				return lines;
			}
			return [];
		};

		default: return function (): string[] {
			var curr: Function | null = (arguments.callee as any).caller,
				FUNC: string = 'function', ANON: string = "{anonymous}",
				fnRE: RegExp = /function\s*([\w\-$]+)?\s*\(/i,
				stack: string[] = [], j: number = 0,
				fn: string, args: any[], i: number;

			while (curr) {
				fn = fnRE.test(curr.toString()) ? RegExp.$1 || ANON : ANON;
				args = stack.slice.call((curr as any).arguments);
				i = args.length;

				while (i--) {
					switch (typeof args[i]) {
						case 'string': args[i] = '"' + args[i].replace(/"/g, '\\"') + '"'; break;
						case 'function': args[i] = FUNC; break;
					}
				}

				stack[j++] = fn + '(' + args.join() + ')';
				curr = (curr as any).caller;
			}

			return stack;
		};
	}

})();

// Export to globalThis for backwards compatibility
const _global = globalThis as any;
_global.isManageMode = isManageMode;
_global.attribNames = attribNames;
_global.waitLoaded = waitLoaded;
_global.isWaiting = isWaiting;
_global.winAttribs = winAttribs;
_global.dialogNames = dialogNames;
_global.dialogAttribs = dialogAttribs;
_global.addAttribs = addAttribs;
_global.DATE_PICKER = DATE_PICKER;
_global.PLEASEWAIT = PLEASEWAIT;
_global.GLOSSARY = GLOSSARY;
_global.DOC_CHECKOUT = DOC_CHECKOUT;
_global.NEWUSER = NEWUSER;
_global.RELATELISTITEM = RELATELISTITEM;
_global.TASK_STATUS = TASK_STATUS;
_global.MEMBERDIRCALENDAR = MEMBERDIRCALENDAR;
_global.MINIHELP = MINIHELP;
_global.RELATEHINT = RELATEHINT;
_global.DELETE_WEBINFO = DELETE_WEBINFO;
_global.MYTEAM_DEMO = MYTEAM_DEMO;
_global.CHAT = CHAT;
_global.FRAME_VIEW = FRAME_VIEW;
_global.ORDER_EDIT = ORDER_EDIT;
_global.DEPENDENCIES = DEPENDENCIES;
_global.EVENT_VIEW = EVENT_VIEW;
_global.PRINT_WINDOW = PRINT_WINDOW;
_global.PREFIX_REQUEST = PREFIX_REQUEST;
_global.RE_ORDER = RE_ORDER;
_global.TEAM_ADDRESS_BOOK = TEAM_ADDRESS_BOOK;
_global.MEDIA_WIN = MEDIA_WIN;
_global.TASK_EDIT = TASK_EDIT;
_global.DOC_CHECKIN = DOC_CHECKIN;
_global.YELLOWBRIX_WIN = YELLOWBRIX_WIN;
_global.SITEMAP_VIEW = SITEMAP_VIEW;
_global.PAGELET_EDIT = PAGELET_EDIT;
_global.GET_RECORD = GET_RECORD;
_global.NORMAL_WIN = NORMAL_WIN;
_global.HELP_ROOT = HELP_ROOT;
_global.HELP = HELP;
_global.COLOR_PICKER_DIALOG = COLOR_PICKER_DIALOG;
_global.SPECTRUM_DIALOG = SPECTRUM_DIALOG;
_global.DATE_PICKER_DIALOG = DATE_PICKER_DIALOG;
_global.FONT_STYLE_PICKER_DIALOG = FONT_STYLE_PICKER_DIALOG;
_global.FONT_SIZE_PICKER_DIALOG = FONT_SIZE_PICKER_DIALOG;
_global.SPELL_CHECKER_DIALOG = SPELL_CHECKER_DIALOG;
_global.SYMBOL_PICKER_DIALOG = SYMBOL_PICKER_DIALOG;
_global.TEXT_EDITOR_DIALOG = TEXT_EDITOR_DIALOG;
_global.TABLE_BUILDER_DIALOG = TABLE_BUILDER_DIALOG;
_global.ZESTY_EDITOR_DIALOG = ZESTY_EDITOR_DIALOG;
_global.DATA_PICKER_DIALOG = DATA_PICKER_DIALOG;
_global.firstField = firstField;
_global.newWindow = newWindow;
_global.newWindowTime = newWindowTime;
_global.APP_ROOT = APP_ROOT;
_global.pleaseWaitComplete = pleaseWaitComplete;
_global.getHelp = getHelp;
_global.doPopup = doPopup;
_global.doPopupNoCenter = doPopupNoCenter;
_global.showDialog = showDialog;
_global.dialogArgs = dialogArgs;
_global.returned = returned;
_global.modalWindow = modalWindow;
_global.modalWindowDone = modalWindowDone;
_global.openMediaLibrary = openMediaLibrary;
_global.openMediaLibraryForEditor = openMediaLibraryForEditor;
_global.uberPrompt = uberPrompt;
_global.doPopupFrame = doPopupFrame;
_global.doPopupAtEvent = doPopupAtEvent;
_global.pleaseWait = pleaseWait;
_global.BASE_STATIC_RESOURCE = BASE_STATIC_RESOURCE;
_global.ImgPreload = ImgPreload;
_global.TRANSPARENT_IMG = TRANSPARENT_IMG;
_global.PLEASE_WAIT_IMG = PLEASE_WAIT_IMG;
_global.CANCEL_IMG = CANCEL_IMG;
_global.SEMI_TRANS_IMG = SEMI_TRANS_IMG;
_global.WAIT_DEFAULT_CONTENT = WAIT_DEFAULT_CONTENT;
_global.WAIT_DEFAULT_CONTENT_MANAGE = WAIT_DEFAULT_CONTENT_MANAGE;
_global.popupWait = popupWait;
_global.doPopIn = doPopIn;
_global.getPopIn = getPopIn;
_global.genericRefresh = genericRefresh;
_global.genericClose = genericClose;
_global.closeWait = closeWait;
_global.popInStack = popInStack;
_global.popInCur = popInCur;
_global.nullFn = nullFn;
_global.getPopInObj = getPopInObj;
_global.focusPopup = focusPopup;
_global.closePopup = closePopup;
_global.realClosePopup = realClosePopup;
_global.glossaryPopup = glossaryPopup;
_global.addCentering = addCentering;
_global.windowOnMainMonitor = windowOnMainMonitor;
_global.makeBetween = makeBetween;
_global.parseValueOf = parseValueOf;
_global.centerLength = centerLength;
_global.addPosition = addPosition;
_global.timeClickURL = timeClickURL;
_global.stripDomain = stripDomain;
_global.goClick = goClick;
_global.cancelClick = cancelClick;
_global.pageWidth = pageWidth;
_global.pageHeight = pageHeight;
_global.pagePosLeft = pagePosLeft;
_global.pagePosTop = pagePosTop;
_global.pagePosRight = pagePosRight;
_global.pagePosBottom = pagePosBottom;
_global.windowPosTop = windowPosTop;
_global.windowPosLeft = windowPosLeft;
_global.screenHeight = screenHeight;
_global.screenWidth = screenWidth;
_global.logIt = logIt;
_global.sendSessionEvent = sendSessionEvent;
_global.sessionEvent = sessionEvent;
_global.serverEvent = serverEvent;
_global.getStackTrace = getStackTrace;

if (_SR_ != null) _SR_.notify("popup.js");

// ES module exports
export {
	isManageMode,
	attribNames,
	waitLoaded,
	isWaiting,
	winAttribs,
	dialogNames,
	dialogAttribs,
	addAttribs,
	DATE_PICKER,
	PLEASEWAIT,
	GLOSSARY,
	DOC_CHECKOUT,
	NEWUSER,
	RELATELISTITEM,
	TASK_STATUS,
	MEMBERDIRCALENDAR,
	MINIHELP,
	RELATEHINT,
	DELETE_WEBINFO,
	MYTEAM_DEMO,
	CHAT,
	FRAME_VIEW,
	ORDER_EDIT,
	DEPENDENCIES,
	EVENT_VIEW,
	PRINT_WINDOW,
	PREFIX_REQUEST,
	RE_ORDER,
	TEAM_ADDRESS_BOOK,
	MEDIA_WIN,
	TASK_EDIT,
	DOC_CHECKIN,
	YELLOWBRIX_WIN,
	SITEMAP_VIEW,
	PAGELET_EDIT,
	GET_RECORD,
	NORMAL_WIN,
	HELP_ROOT,
	HELP,
	COLOR_PICKER_DIALOG,
	SPECTRUM_DIALOG,
	DATE_PICKER_DIALOG,
	FONT_STYLE_PICKER_DIALOG,
	FONT_SIZE_PICKER_DIALOG,
	SPELL_CHECKER_DIALOG,
	SYMBOL_PICKER_DIALOG,
	TEXT_EDITOR_DIALOG,
	TABLE_BUILDER_DIALOG,
	ZESTY_EDITOR_DIALOG,
	DATA_PICKER_DIALOG,
	firstField,
	newWindow,
	newWindowTime,
	APP_ROOT,
	pleaseWaitComplete,
	getHelp,
	doPopup,
	doPopupNoCenter,
	showDialog,
	dialogArgs,
	returned,
	modalWindow,
	modalWindowDone,
	openMediaLibrary,
	openMediaLibraryForEditor,
	uberPrompt,
	doPopupFrame,
	doPopupAtEvent,
	pleaseWait,
	BASE_STATIC_RESOURCE,
	ImgPreload,
	TRANSPARENT_IMG,
	PLEASE_WAIT_IMG,
	CANCEL_IMG,
	SEMI_TRANS_IMG,
	WAIT_DEFAULT_CONTENT,
	WAIT_DEFAULT_CONTENT_MANAGE,
	popupWait,
	doPopIn,
	getPopIn,
	genericRefresh,
	genericClose,
	closeWait,
	popInStack,
	popInCur,
	nullFn,
	getPopInObj,
	focusPopup,
	closePopup,
	realClosePopup,
	glossaryPopup,
	addCentering,
	windowOnMainMonitor,
	makeBetween,
	parseValueOf,
	centerLength,
	addPosition,
	timeClickURL,
	stripDomain,
	goClick,
	cancelClick,
	pageWidth,
	pageHeight,
	pagePosLeft,
	pagePosTop,
	pagePosRight,
	pagePosBottom,
	windowPosTop,
	windowPosLeft,
	screenHeight,
	screenWidth,
	logIt,
	sendSessionEvent,
	sessionEvent,
	serverEvent,
	getStackTrace
};
export type { PopInObject, ImgPreloadInstance, DialogArgs, MediaListener, MediaListenerManager };
